import { useRuntimeConfig, useState } from '#imports'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useIsE2ETestingEnvironment } from '@backmarket/nuxt-module-qa/useIsE2ETestingEnvironment'

import { useNethone } from './useNethone'

/**
 * Fields that should never be captured by Nethone across all pages.
 * These are password and other sensitive input fields.
 *
 * These fields are centralized here because fingerprinting only initializes once
 * per session. If a user lands on any page first, all secret fields from all pages
 * must be registered at that moment to ensure they're protected during navigation.
 */
const SECRET_FIELDS = [
  // Password reset page
  'newPassword',
  'newPasswordConfirmation',
]

/**
 * Initialize Nethone fraud detection script on a specific page.
 * The fingerprinting state is shared globally - once initialized on any page,
 * it won't re-initialize on subsequent page navigations.
 *
 * @param pageName - Identifier for the page (used for logging only)
 */
export function useNethoneOnPage(pageName: string) {
  const logger = useLogger()

  const config = useRuntimeConfig()
  const isEnabled = config.public.FF_NETHONE_ENABLED

  const isE2ETestingEnvironment = useIsE2ETestingEnvironment()

  const hasStartedFingerprinting = useState(
    'nethone-fingerprinting',
    () => false,
  )

  if (!isEnabled || isE2ETestingEnvironment || hasStartedFingerprinting.value) {
    return
  }

  const loadStartTime = performance.now()

  const { onLoaded, initialize } = useNethone()

  const onScriptLoaded = async () => {
    const loadTime = performance.now() - loadStartTime
    logger.info(`Nethone script loaded on ${pageName}`, {
      owners: ['bot-squad-identity'],
      loadTimeMs: loadTime,
    })

    const profileStartTime = performance.now()

    try {
      await initialize({
        sensitiveFields: [],
        secretFields: SECRET_FIELDS,
      })

      hasStartedFingerprinting.value = true

      const profileTime = performance.now() - profileStartTime
      logger.info(`Nethone profiling completed on ${pageName}`, {
        owners: ['bot-squad-identity'],
        profileTimeMs: profileTime,
      })
    } catch (err: unknown) {
      const profileTime = performance.now() - profileStartTime
      logger.error(`Nethone profiling failed on ${pageName}`, {
        owners: ['bot-squad-identity'],
        profileTimeMs: profileTime,
        error: err instanceof Error ? err : new Error(String(err)),
      })
    }
  }

  onLoaded(onScriptLoaded)
}
